import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Alert,
  AlertIcon,
  Spinner,
  Button,
  Input,
  Heading,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  useBreakpointValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Card,
  IconButton,
  Text,
  Flex,
  Badge,
  Avatar,
  Select,
  Textarea,
  SimpleGrid,
  Checkbox,
  VStack,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiPlus, FiEdit2, FiTrash2,
  FiDownload, FiUpload
} from 'react-icons/fi';
import { ContactsAPI } from '../../services/api';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../i18n';
import { getPortalPageCopy } from '../../portalPageCopy';

/** CSV export/import columns (unified contacts). */
const CSV_COLUMNS = [
  'name', 'other_names', 'contact_type', 'extension', 'phone', 'email', 'department', 'title', 'voicemail_enabled',
  'supports_consultation_transfer', 'is_operator', 'status', 'notes'
];

const VALID_CONTACT_TYPES = ['employee', 'department', 'queue', 'client', 'visitor'];

function normalizeOtherNames(value) {
  if (Array.isArray(value)) {
    return value.map(v => String(v || '').trim()).filter(Boolean);
  }
  return String(value || '')
    .split(/[\n,;]+/)
    .map(v => v.trim())
    .filter(Boolean);
}

function getContactOtherNames(contact) {
  return normalizeOtherNames(contact?.other_names || contact?.meta?.other_names || contact?.meta?.aliases || []);
}

function formatOtherNames(value) {
  return normalizeOtherNames(value).join(', ');
}

/** Align with backend communication_api defaults; UI no longer edits these flags. */
function applyContactRoutingDefaults(data) {
  const t = data.contact_type || 'visitor';
  let can_receive_visitor_transfer = false;
  let can_receive_client_transfer = false;
  if (t === 'employee') {
    can_receive_visitor_transfer = true;
    can_receive_client_transfer = true;
  } else if (t === 'client') {
    can_receive_visitor_transfer = true;
    can_receive_client_transfer = false;
  }
  return {
    ...data,
    other_names: normalizeOtherNames(data.other_names),
    can_receive_visitor_transfer,
    can_receive_client_transfer,
    allow_blind_from_external: false,
  };
}

function formatStatusLabel(status, copy) {
  const s = (status || 'active').toLowerCase();
  if (s === 'on_leave') return copy.onLeave;
  if (s === 'prospect') return copy.prospect;
  if (s === 'active') return copy.active;
  if (s === 'inactive') return copy.inactive;
  return status || 'active';
}

function formatContactTypeLabel(type, copy) {
  const t = (type || 'visitor').toLowerCase();
  if (t === 'employee') return copy.employee;
  if (t === 'department') return copy.department;
  if (t === 'queue') return copy.queue;
  if (t === 'client') return copy.client;
  return copy.visitor;
}

function contactTypeColor(type) {
  const t = (type || '').toLowerCase();
  if (t === 'employee') return 'blue';
  if (t === 'department') return 'purple';
  if (t === 'queue') return 'teal';
  if (t === 'client') return 'cyan';
  return 'gray';
}

function statusColor(status) {
  const s = (status || 'active').toLowerCase();
  if (s === 'active') return 'green';
  if (s === 'inactive') return 'red';
  if (s === 'on_leave' || s === 'prospect') return 'yellow';
  return 'gray';
}

function isTransferTargetType(type) {
  return ['employee', 'department', 'queue'].includes((type || '').toLowerCase());
}

const FieldItem = ({ label, children, mono = false }) => (
  <Box minW={0}>
    <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="0">
      {label}
    </Text>
    <Tooltip label={children || ''} isDisabled={!children}>
      <Text
        mt={0.5}
        fontSize="sm"
        color="gray.800"
        fontFamily={mono ? 'mono' : 'inherit'}
        minW={0}
        noOfLines={1}
        title={children || undefined}
      >
        {children || '-'}
      </Text>
    </Tooltip>
  </Box>
);

const FormSection = ({ title, children }) => (
  <Box borderTop="1px" borderColor="gray.200" pt={4}>
    <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={3}>
      {title}
    </Text>
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
      {children}
    </SimpleGrid>
  </Box>
);

const ContactForm = ({ isOpen, onClose, onSubmit, initialValues, type, copy }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const savingRef = useRef(false);

  const effectiveType = type || 'employee';
  const isDeptOrQueue = (t) => t === 'department' || t === 'queue';

  useEffect(() => {
    const nextValues = initialValues
      ? { ...initialValues, other_names: formatOtherNames(getContactOtherNames(initialValues)) }
      : {
          name: '',
          other_names: '',
          phone: '',
          extension: '',
          email: '',
          title: '',
          department: '',
          voicemail_enabled: false,
          supports_consultation_transfer: !isDeptOrQueue(effectiveType),
          contact_type: effectiveType,
          status: 'active',
          notes: '',
          meta: {},
          is_operator: false,
        };
    setFormData(nextValues);
    setErrors({});
    setSaveError(false);
  }, [initialValues, isOpen, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleMetaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      meta: {
        ...prev.meta,
        [field]: value
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = copy.nameRequired;
    if (isTransferTargetType(formData.contact_type || effectiveType) && !formData.extension?.trim()) {
      newErrors.extension = copy.extensionRequired;
    }
    if (!isTransferTargetType(formData.contact_type || effectiveType) && !formData.phone?.trim() && !formData.extension?.trim()) {
      newErrors.phone = copy.phoneOrExtensionRequired;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (savingRef.current || !validateForm()) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    try {
      if (await onSubmit(applyContactRoutingDefaults(formData))) onClose();
      else setSaveError(true);
    } catch {
      setSaveError(true);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const renderSpecificFields = () => {
    const currentType = formData.contact_type || effectiveType;
    switch (currentType) {
      case 'department':
      case 'queue':
        return (
          <FormControl gridColumn={{ base: 'auto', md: 'span 2' }}>
            <FormLabel>{copy.status}</FormLabel>
            <Select
              value={formData.status || 'active'}
              onChange={(e) => handleInputChange('status', e.target.value)}
            >
              <option value="active">{copy.activeTransferTarget}</option>
              <option value="inactive">{copy.inactiveHidden}</option>
            </Select>
          </FormControl>
        );
      case 'employee':
        return (
          <>
            <FormControl>
              <FormLabel>{copy.department}</FormLabel>
              <Input
                value={formData.department || ''}
                onChange={(e) => handleInputChange('department', e.target.value)}
                placeholder={copy.departmentPlaceholder}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{copy.positionTitle}</FormLabel>
              <Input
                value={formData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder={copy.positionPlaceholder}
              />
            </FormControl>
            <FormControl gridColumn="span 2">
              <FormLabel>{copy.status}</FormLabel>
              <Select
                value={formData.status || 'active'}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <option value="active">{copy.activeTransfers}</option>
                <option value="on_leave">{copy.leaveUnavailable}</option>
                <option value="inactive">{copy.inactiveNotOffered}</option>
              </Select>
            </FormControl>
          </>
        );
      case 'client':
        return (
          <>
            <FormControl>
              <FormLabel>{copy.companyName}</FormLabel>
              <Input 
                value={formData.meta?.company_name || ''} 
                onChange={(e) => handleMetaChange('company_name', e.target.value)} 
                placeholder={copy.companyPlaceholder}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{copy.industry}</FormLabel>
              <Input 
                value={formData.meta?.industry || ''} 
                onChange={(e) => handleMetaChange('industry', e.target.value)} 
                placeholder={copy.industryPlaceholder}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{copy.tier}</FormLabel>
              <Select 
                value={formData.meta?.tier || 'standard'} 
                onChange={(e) => handleMetaChange('tier', e.target.value)}
              >
                <option value="standard">{copy.standard}</option>
                <option value="vip">VIP</option>
                <option value="partner">{copy.partner}</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>{copy.status}</FormLabel>
              <Select
                value={formData.status || 'active'}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <option value="active">{copy.active}</option>
                <option value="prospect">{copy.prospect}</option>
                <option value="inactive">{copy.inactive}</option>
              </Select>
            </FormControl>
          </>
        );
      case 'supplier':
        return (
          <>
            <FormControl>
              <FormLabel>{copy.companyName}</FormLabel>
              <Input 
                value={formData.meta?.company_name || ''} 
                onChange={(e) => handleMetaChange('company_name', e.target.value)} 
                placeholder={copy.supplierPlaceholder}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{copy.serviceType}</FormLabel>
              <Input 
                value={formData.meta?.service_type || ''} 
                onChange={(e) => handleMetaChange('service_type', e.target.value)} 
                placeholder={copy.servicePlaceholder}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{copy.contractExpiry}</FormLabel>
              <Input 
                type="date"
                value={formData.meta?.contract_expiry || ''} 
                onChange={(e) => handleMetaChange('contract_expiry', e.target.value)} 
              />
            </FormControl>
          </>
        );
      case 'visitor':
      default:
        return (
          <FormControl gridColumn="span 2">
            <FormLabel>{copy.status}</FormLabel>
            <Select
              value={formData.status || 'active'}
              onChange={(e) => handleInputChange('status', e.target.value)}
            >
              <option value="active">{copy.active}</option>
              <option value="inactive">{copy.inactive}</option>
            </Select>
          </FormControl>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} size="3xl" closeOnOverlayClick={!saving} closeOnEsc={!saving}>
      <ModalOverlay />
      <ModalContent borderRadius="md" overflow="hidden">
        <ModalHeader borderBottom="1px" borderColor="gray.200" py={4}>
          <Text fontSize="lg" fontWeight="semibold">
            {initialValues ? copy.editContact : `${copy.new} ${formatContactTypeLabel(type, copy)}`}
          </Text>
          <Text fontSize="sm" color="gray.500" fontWeight="normal" mt={1}>
            {copy.formSummary}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={5}>
          {saveError && <Alert status="error" mb={4}><AlertIcon />{copy.saveFailed}</Alert>}
          <Box as="fieldset" disabled={saving}>
          <VStack align="stretch" spacing={5}>
            <FormSection title={copy.routingIdentity}>
            <FormControl isInvalid={errors.name} gridColumn="span 2">
              <FormLabel>{copy.name}</FormLabel>
              <Input 
                value={formData.name || ''} 
                onChange={(e) => handleInputChange('name', e.target.value)} 
                placeholder={copy.fullNamePlaceholder}
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl gridColumn="span 2">
              <FormLabel>{copy.otherNames}</FormLabel>
              <Textarea
                value={formData.other_names || ''}
                onChange={(e) => handleInputChange('other_names', e.target.value)}
                placeholder={copy.aliasesPlaceholder}
                rows={2}
              />
            </FormControl>

            <FormControl isInvalid={errors.extension}>
              <FormLabel>{copy.extensionNumber}</FormLabel>
              <Input
                value={formData.extension || ''}
                onChange={(e) => handleInputChange('extension', e.target.value)}
                placeholder="1003"
                inputMode="numeric"
                autoComplete="off"
              />
              <FormErrorMessage>{errors.extension}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>{copy.contactType}</FormLabel>
              <Select
                value={formData.contact_type || effectiveType}
                onChange={(e) => {
                  const newType = e.target.value;
                  const deptOrQueue = newType === 'department' || newType === 'queue';
                  handleInputChange('contact_type', newType);
                  if (deptOrQueue) {
                    handleInputChange('supports_consultation_transfer', false);
                  } else if (newType === 'employee') {
                    handleInputChange('supports_consultation_transfer', true);
                  }
                }}
              >
                <option value="employee">{copy.employee}</option>
                <option value="department">{copy.department}</option>
                <option value="queue">{copy.queue}</option>
                <option value="client">{copy.client}</option>
                <option value="visitor">{copy.visitor}</option>
              </Select>
            </FormControl>
            </FormSection>

            <FormSection title={copy.contactDetails}>
            <FormControl isInvalid={errors.phone}>
              <FormLabel>{copy.phoneNumber}</FormLabel>
              <Input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+1 (555) 000-0000"
                autoComplete="tel"
              />
              <FormErrorMessage>{errors.phone}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>{copy.email}</FormLabel>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={copy.emailPlaceholder}
                autoComplete="email"
                spellCheck={false}
              />
            </FormControl>

            {renderSpecificFields()}

            <FormControl gridColumn="span 2">
              <FormLabel>{copy.notes}</FormLabel>
              <Textarea
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder={copy.notesPlaceholder}
                rows={2}
              />
            </FormControl>
            </FormSection>

            <FormSection title={copy.runtimeBehavior}>
              <Box gridColumn={{ base: 'auto', md: 'span 2' }}>
              <VStack align="stretch" spacing={3}>
                {(formData.contact_type === 'employee' ||
                  formData.contact_type === 'department' ||
                  formData.contact_type === 'queue') && (
                  <Checkbox
                    isChecked={formData.supports_consultation_transfer !== false}
                    onChange={(e) => handleInputChange('supports_consultation_transfer', e.target.checked)}
                    colorScheme="brand"
                  >
                    <Text fontSize="sm">
                      {copy.supportsConsultation}
                    </Text>
                  </Checkbox>
                )}
                {formData.voicemail_enabled && <Text fontSize="sm" color="gray.600">{copy.voicemailManagedExternally}</Text>}
                <Checkbox
                  isChecked={formData.is_operator || false}
                  onChange={(e) => handleInputChange('is_operator', e.target.checked)}
                  colorScheme="orange"
                >
                  <Text fontSize="sm">{copy.operatorAnyTransfer}</Text>
                </Checkbox>
              </VStack>
              </Box>
            </FormSection>
          </VStack>
          </Box>
        </ModalBody>
        <ModalFooter borderTop="1px" borderColor="gray.200" bg="gray.50">
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>{copy.cancel}</Button>
          <Button colorScheme="brand" onClick={handleSubmit} isLoading={saving}>{copy.saveContact}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ContactsPage = () => {
  const { lang } = useLanguage();
  const copy = getPortalPageCopy(lang, 'contacts');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const requestSequence = useRef(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCsvOpen, setIsCsvOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = useBreakpointValue({ base: 5, md: 8, lg: 10 }) ?? 8;
  const uploadInputRef = useRef(null);
  const toast = useToast();
  const cancelRef = useRef();

  const fetchContacts = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setLoadError(false);
    try {
      const list = await ContactsAPI.list();
      if (sequence === requestSequence.current) setContacts(Array.isArray(list) ? list : []);
    } catch (error) {
      if (sequence === requestSequence.current) setLoadError(true);
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
    const sequenceRef = requestSequence;
    return () => { sequenceRef.current++; };
  }, [fetchContacts]);

  const handleAdd = () => {
    setSelectedContact(null);
    setIsModalOpen(true);
  };

  const formType = selectedContact?.contact_type || 'employee';

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setIsModalOpen(true);
  };

  const handleDelete = (contact) => {
    setContactToDelete(contact);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await ContactsAPI.remove(contactToDelete.id);
      setContacts(contacts.filter(c => c.id !== contactToDelete.id));
      toast({ title: copy.deleted, status: 'success' });
    } catch (error) {
      toast({ title: copy.deleteFailed, status: 'error' });
    }
    setIsDeleteOpen(false);
  };

  const handleSave = async (data) => {
    try {
      const { api } = require('../../services/api');
      let savedContact;
      if (selectedContact) {
        const res = await api.put(`/contacts/${selectedContact.id}`, data);
        savedContact = res.data;
        setContacts(contacts.map(c => c.id === savedContact.id ? savedContact : c));
        toast({ title: copy.updated, status: 'success' });
      } else {
        const res = await api.post('/contacts', data);
        savedContact = res.data;
        setContacts([...contacts, savedContact]);
        toast({ title: copy.created, status: 'success' });
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const filteredContacts = contacts.filter(c => {
    const otherNamesText = getContactOtherNames(c).join(' ').toLowerCase();
    const matchesSearch = !search.trim() ||
      (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
      otherNamesText.includes(search.toLowerCase()) ||
      (c.phone || '').includes(search) ||
      (c.extension || '').includes(search) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.department || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      !statusFilter ||
      (c.status || 'active').toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);

  const renderRow = (contact) => (
    <Box
      key={contact.id}
      borderBottom="1px"
      borderColor="gray.100"
      borderRadius="md"
      bg="white"
      px={{ base: 3, md: 4 }}
      py={{ base: 3, md: 2.5 }}
      _hover={{ bg: 'gray.50' }}
    >
      <Box
        display="grid"
        gridTemplateColumns={{
          base: '1fr',
          lg: 'minmax(260px, 1.5fr) minmax(0, 3.6fr) auto',
        }}
        gap={{ base: 3, lg: 4 }}
        alignItems="center"
      >
        <HStack spacing={3} minW={0}>
          <Avatar size="sm" name={contact.name} src={contact.avatar} />
          <Box minW={0} flex="1">
            <HStack spacing={2} flexWrap="wrap">
              <Tooltip label={contact.name || ''} isDisabled={!contact.name}>
                <Text fontWeight="semibold" fontSize="sm" color="gray.900" minW={0} noOfLines={1}>
                  {contact.name}
                </Text>
              </Tooltip>
              <Badge fontSize="xs" colorScheme={contactTypeColor(contact.contact_type)} variant="subtle" whiteSpace="nowrap">
                {formatContactTypeLabel(contact.contact_type, copy)}
              </Badge>
              <Badge fontSize="xs" colorScheme={statusColor(contact.status)} variant="outline" whiteSpace="nowrap">
                {formatStatusLabel(contact.status, copy)}
              </Badge>
            </HStack>
            <Tooltip label={formatOtherNames(getContactOtherNames(contact)) || contact.title || contact.department || ''}>
              <Text fontSize="xs" color="gray.500" mt={1} minW={0} noOfLines={1}>
                {formatOtherNames(getContactOtherNames(contact)) || contact.title || contact.department || copy.noAliases}
              </Text>
            </Tooltip>
          </Box>
        </HStack>

        <Box
          display="grid"
          gridTemplateColumns={{
            base: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
            xl: '0.7fr 1fr 1fr 1.25fr 1fr 0.8fr',
          }}
          gap={{ base: 2.5, md: 3 }}
          minW={0}
          alignItems="start"
        >
          <FieldItem label={copy.extension} mono>{contact.extension}</FieldItem>
          <FieldItem label={copy.department}>{contact.department}</FieldItem>
          <FieldItem label={copy.phone}>{contact.phone}</FieldItem>
          <FieldItem label={copy.email}>{contact.email}</FieldItem>
          <Box minW={0}>
            <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="0">
              {copy.transfer}
            </Text>
            <Badge
              mt={0.5}
              fontSize="xs"
              colorScheme={contact.supports_consultation_transfer !== false ? 'green' : 'orange'}
              variant="subtle"
              whiteSpace="nowrap"
              maxW="100%"
              overflow="hidden"
              textOverflow="ellipsis"
              title={contact.supports_consultation_transfer !== false ? copy.consultationTransfer : copy.blindTransferOnly}
            >
              {contact.supports_consultation_transfer !== false ? copy.consultation : copy.blindOnly}
            </Badge>
          </Box>
          <Box minW={0}>
            <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="0">
              {copy.flags}
            </Text>
            <HStack mt={0.5} spacing={1} flexWrap="wrap">
              {contact.voicemail_enabled && <Badge fontSize="xs" colorScheme="green" variant="subtle" whiteSpace="nowrap">{copy.voicemail}</Badge>}
              {contact.is_operator && <Badge fontSize="xs" colorScheme="orange" variant="subtle" whiteSpace="nowrap">{copy.operator}</Badge>}
              {!contact.voicemail_enabled && !contact.is_operator && <Text fontSize="sm" color="gray.500">-</Text>}
            </HStack>
          </Box>
        </Box>

        <HStack spacing={1} justify={{ base: 'flex-start', lg: 'flex-end' }} flexShrink={0}>
          <IconButton size="sm" icon={<FiEdit2 />} variant="ghost" onClick={() => handleEdit(contact)} aria-label={copy.edit} />
          <IconButton size="sm" icon={<FiTrash2 />} variant="ghost" colorScheme="red" onClick={() => handleDelete(contact)} aria-label={copy.delete} />
        </HStack>
      </Box>
    </Box>
  );

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / pageSize));
  const paginatedContacts = filteredContacts.slice(page * pageSize, (page + 1) * pageSize);

  const downloadContactsTemplate = () => {
    const headerRow = CSV_COLUMNS.join(',');
    const exampleRows = [
      ['Alice Chen', 'Alice, Ms Chen', 'employee', '1003', '+15551234', 'alice@example.com', 'Product', 'Engineer', 'yes', 'yes', 'no', 'active', ''],
      ['Bob Smith', 'Bob, Mr Smith', 'employee', '1004', '', 'bob@example.com', 'Sales', 'Manager', 'yes', 'yes', 'no', 'active', ''],
      ['Sales Queue', 'Sales, New Sales', 'queue', '2001', '', '', '', '', 'no', 'no', 'no', 'active', 'Main sales ring group'],
      ['Support Dept', 'Support, Help Desk', 'department', '2002', '', '', '', '', 'no', 'no', 'no', 'active', 'Support department'],
    ].map(row => row.map(v => (v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v)).join(','));
    const csv = [headerRow, ...exampleRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'contacts_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const downloadCurrentContacts = () => {
    const toCsvValue = (v) => {
      const s = (v == null ? '' : String(v)).trim();
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = filteredContacts.map(c => [
      c.name ?? '',
      formatOtherNames(getContactOtherNames(c)),
      c.contact_type ?? 'employee',
      c.extension ?? '',
      c.phone ?? '',
      c.email ?? '',
      c.department ?? '',
      c.title ?? '',
      c.voicemail_enabled ? 'yes' : 'no',
      c.supports_consultation_transfer !== false ? 'yes' : 'no',
      c.is_operator ? 'yes' : 'no',
      c.status ?? 'active',
      c.notes ?? '',
    ].map(toCsvValue));
    const csv = [CSV_COLUMNS.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `contacts_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  useEffect(() => {
    if (page >= totalPages && totalPages > 0) setPage(0);
  }, [page, totalPages]);

  const tableBody = () => {
    if (loading) return <Spinner aria-label={lang === 'zh' ? '加载中' : 'Loading'} />;
    if (loadError) return <Alert status="error"><AlertIcon />{copy.loadFailed}<Button ml={3} onClick={fetchContacts}>{lang === 'zh' ? '重试' : 'Retry'}</Button></Alert>;
    if (filteredContacts.length > 0) return paginatedContacts.map(renderRow);
    return (
      <Box border="1px" borderColor="gray.200" borderRadius="md" bg="white" py={10} textAlign="center">
        <VStack spacing={3}>
          <Text color="gray.500">{copy.noMatches}</Text>
          <Button size="sm" colorScheme="brand" variant="outline" onClick={handleAdd}>{copy.addContact}</Button>
        </VStack>
      </Box>
    );
  };

  return (
    <Box h="100%" minH={0} display="flex" flexDirection="column" overflow="hidden">
      <Box flex="1" minH={0} overflowY="auto" p={{ base: 3, md: 4 }} bg="gray.50">
      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb={3} gap={3} wrap="wrap" flexShrink={0}>
        <Box>
          <Heading size="lg" color="gray.900">{copy.pageTitle}</Heading>
          <Text fontSize="sm" color="gray.600" mt={0.5}>
            {copy.pageSummary}
          </Text>
        </Box>
        <HStack spacing={2} flexWrap="wrap" justify={{ base: 'flex-start', md: 'flex-end' }}>
          <Button leftIcon={<FiPlus />} size={{ base: "sm", md: "md" }} colorScheme="brand" onClick={handleAdd}>{copy.addContact}</Button>
          <Button variant="outline" size="sm" leftIcon={<FiUpload />} onClick={() => setIsCsvOpen(true)}>
            {copy.csvMaintenance}
          </Button>
          <input
            type="file"
            ref={uploadInputRef}
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              const normalizeHeader = (value) => (value || '').toString().trim().toLowerCase();
              const toBool = (value) => {
                if (typeof value === 'boolean') return value;
                const normalized = (value || '').toString().trim().toLowerCase();
                return ['true', 'yes', 'y', '1'].includes(normalized);
              };

              const parseRows = async () => {
                if (file.name.toLowerCase().endsWith('.csv')) {
                  const text = await file.text();
                  const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
                  return parsed.data || [];
                }
                const buffer = await file.arrayBuffer();
                const workbook = XLSX.read(buffer, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                return XLSX.utils.sheet_to_json(sheet, { defval: '' });
              };

              const mapRow = (row) => {
                const normalized = Object.keys(row || {}).reduce((acc, key) => {
                  acc[normalizeHeader(key)] = row[key];
                  return acc;
                }, {});

                const extension = (normalized['extension'] || normalized['ext'] || '').toString().trim();
                const name =
                  (normalized['name'] || '').toString().trim() ||
                  (extension ? `${copy.extension} ${extension}` : copy.unknown);

                const rawType = (normalized['contact_type'] || 'employee').toString().trim().toLowerCase();
                const contactType = VALID_CONTACT_TYPES.includes(rawType) ? rawType : 'employee';

                const meta = {};
                if (normalized['meta_employee_id']) meta.employee_id = normalized['meta_employee_id'];
                if (normalized['meta_company_name']) meta.company_name = normalized['meta_company_name'];
                if (normalized['meta_industry']) meta.industry = normalized['meta_industry'];
                if (normalized['meta_tier']) meta.tier = normalized['meta_tier'];
                if (normalized['meta_service_type']) meta.service_type = normalized['meta_service_type'];
                if (normalized['meta_contract_expiry']) meta.contract_expiry = normalized['meta_contract_expiry'];

                return applyContactRoutingDefaults({
                  name,
                  other_names: (normalized['other_names'] || normalized['other names'] || normalized['aliases'] || '').toString().trim(),
                  phone: (normalized['phone'] || '').toString().trim() || null,
                  extension: extension || null,
                  email: (normalized['email'] || normalized['email address'] || '').toString().trim(),
                  title: (normalized['title'] || '').toString().trim(),
                  department: (normalized['department'] || '').toString().trim(),
                  voicemail_enabled: toBool(normalized['vmail enabled'] || normalized['voicemail_enabled']),
                  supports_consultation_transfer: normalized['supports_consultation_transfer'] !== undefined
                    ? toBool(normalized['supports_consultation_transfer'])
                    : !(contactType === 'department' || contactType === 'queue'),
                  contact_type: contactType,
                  is_operator: toBool(normalized['is_operator']),
                  status: (normalized['status'] || 'active').toString().trim(),
                  notes: (normalized['notes'] || '').toString().trim(),
                  meta,
                });
              };

              (async () => {
                try {
                  const rows = await parseRows();
                  if (!rows.length) {
                    throw new Error(copy.noRows);
                  }
                  const payload = rows.map(mapRow);
                  // Row-level validation: required fields, format, duplicate extension
                  const extensionsSeen = new Set();
                  const errorReport = [];
                  const validPayload = [];
                  for (let i = 0; i < payload.length; i++) {
                    const row = payload[i];
                    const rowNum = i + 2;
                    const errs = [];
                    if (!(row.name && row.name.trim())) errs.push(copy.nameRequired);
                    if (row.contact_type && !VALID_CONTACT_TYPES.includes(row.contact_type)) {
                      errs.push(`${copy.invalidContactType} ${VALID_CONTACT_TYPES.join(', ')}`);
                    }
                    if (row.extension != null && row.extension !== '') {
                      const ext = String(row.extension).trim();
                      if (!/^[0-9a-zA-Z-]+$/.test(ext)) errs.push(copy.invalidExtension);
                      if (extensionsSeen.has(ext)) errs.push(copy.duplicateExtension);
                      else extensionsSeen.add(ext);
                    }
                    if (row.phone != null && row.phone !== '' && !/^[\d\s\-+().]+$/.test(String(row.phone).trim())) {
                      errs.push(copy.invalidPhone);
                    }
                    if (errs.length) {
                      errorReport.push({ row: rowNum, name: row.name || '', extension: row.extension || '', errors: errs.join('; ') });
                    } else {
                      validPayload.push(row);
                    }
                  }
                  if (errorReport.length > 0) {
                    const msg = `${errorReport.length} ${copy.rowsHaveErrors} ${validPayload.length} ${copy.validRowsImported}`;
                    toast({
                      title: copy.validationIssues,
                      description: msg,
                      status: 'warning',
                      duration: 6000,
                      isClosable: true,
                    });
                    const downloadReport = () => {
                      const csv = ['Row,Name,Extension,Errors', ...errorReport.map(e => `"${e.row}","${(e.name || '').replace(/"/g, '""')}","${e.extension}","${(e.errors || '').replace(/"/g, '""')}"`)].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      link.href = URL.createObjectURL(blob);
                      link.setAttribute('download', `contacts_import_errors_${new Date().toISOString().slice(0, 10)}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(link.href);
                    };
                    downloadReport();
                  }
                  if (validPayload.length === 0) {
                    toast({ title: copy.importSkipped, description: copy.importSkippedDetail, status: 'error', duration: 4000, isClosable: true });
                    if (event.target) event.target.value = '';
                    return;
                  }
                  const result = await ContactsAPI.bulkImport(validPayload);
                  await fetchContacts();
                  setIsCsvOpen(false);
                  toast({
                    title: copy.importFinished,
                    description: `${copy.imported} ${result.success} ${copy.contacts}${result.failed ? `, ${result.failed} ${copy.failedCount}` : ''}${errorReport.length ? `; ${errorReport.length} ${copy.skippedRows}` : ''}`,
                    status: result.failed ? 'warning' : 'success',
                    duration: 4000,
                    isClosable: true,
                  });
                } catch (err) {
                  toast({ title: copy.uploadFailed, description: err.message, status: 'error', duration: 4000, isClosable: true });
                } finally {
                  if (event.target) event.target.value = '';
                }
              })();
            }}
          />
        </HStack>
      </Flex>

      <Card flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden" borderRadius="md" boxShadow="none" border="1px" borderColor="gray.200">
        <Box
          px={{ base: 3, md: 4 }}
          py={3}
          borderBottom="1px"
          borderColor="gray.200"
          bg="gray.100"
          flexShrink={0}
        >
          <Flex gap={3} wrap="wrap" align="flex-end" justify="space-between">
            <HStack spacing={3} flexWrap="wrap" align="flex-end">
            <FormControl w={{ base: '100%', md: '340px' }}>
              <FormLabel fontSize="xs" mb={1}>
                {copy.search}
              </FormLabel>
            <Input
              size="sm"
              placeholder={copy.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            </FormControl>
            <FormControl w={{ base: '100%', sm: '180px' }}>
              <FormLabel fontSize="xs" mb={1}>
                {copy.status}
              </FormLabel>
              <Select
                size="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">{copy.allStatuses}</option>
                <option value="active">{copy.active}</option>
                <option value="inactive">{copy.inactive}</option>
                <option value="on_leave">{copy.onLeave}</option>
                <option value="prospect">{copy.prospect}</option>
              </Select>
            </FormControl>
            </HStack>
            <Text fontSize="sm" color="gray.600" pb={{ base: 0, md: 1 }}>
              {loading || loadError ? '—' : filteredContacts.length} {copy.contacts}
            </Text>
          </Flex>
        </Box>

        <Box flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden">
          <VStack align="stretch" spacing={0} flex="1" minH={0} overflowY="auto" bg="white">
            {tableBody()}
          </VStack>

          <HStack px={{ base: 3, md: 4 }} py={3} justify="space-between" align="center" flexWrap="wrap" flexShrink={0} borderTop="1px" borderColor="gray.200" bg="white">
            <Text fontSize="sm" color="gray.500">
              {copy.page} {page + 1} {copy.of} {totalPages}
            </Text>
            <HStack spacing={2}>
              <Button size="sm" isDisabled={page === 0} onClick={() => setPage((p) => p - 1)}>{copy.previous}</Button>
              <Button size="sm" isDisabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>{copy.next}</Button>
            </HStack>
          </HStack>
        </Box>
      </Card>

      <ContactForm
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSave}
        initialValues={selectedContact}
        type={formType}
        copy={copy}
      />

      <Modal isOpen={isCsvOpen} onClose={() => setIsCsvOpen(false)} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{copy.csvMaintenance}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.700">
                {copy.csvDescription}
              </Text>
              <Box border="1px" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                <Text fontSize="sm" fontWeight="semibold" mb={1}>{copy.acceptedFormats}</Text>
                <Text fontSize="sm" color="gray.600">
                  {copy.acceptedFormatsDetail}
                </Text>
              </Box>
              <HStack spacing={3} flexWrap="wrap">
                <Button leftIcon={<FiDownload />} variant="outline" onClick={downloadContactsTemplate}>
                  {copy.downloadTemplate}
                </Button>
                <Button leftIcon={<FiDownload />} variant="outline" onClick={downloadCurrentContacts}>
                  {copy.downloadCurrent}
                </Button>
                <Button leftIcon={<FiUpload />} colorScheme="brand" onClick={() => uploadInputRef.current?.click()}>
                  {copy.uploadFile}
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setIsCsvOpen(false)}>{copy.close}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={() => {
        setIsDeleteOpen(false);
        setContactToDelete(null);
      }}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{copy.deleteTitle}</AlertDialogHeader>
            <AlertDialogBody>
              {copy.deleteConfirmPrefix} {contactToDelete?.name}? {copy.deleteConfirmSuffix}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => { setIsDeleteOpen(false); setContactToDelete(null); }}>{copy.cancel}</Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>{copy.deleteAction}</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      </Box>
    </Box>
  );
};

export default ContactsPage;
